import React, { Fragment } from 'react';

import { connect } from 'react-redux';
import { PrimaryButton, SecondaryButton, DestructiveButton, Dialog as ComponentDialog } from '@dev/ee-components';
import { makeStyles } from '@material-ui/core/styles';

import { DialogProps } from './DialogInterface';

import { ApplicationState as State } from '@api/store';
import { HideDialog } from '@api/store/dialog/actions';
import { Portal } from '@material-ui/core';
import { useAvailableDialogsConfig } from './useAvailableDialogs';
import { DialogIds } from './DialogIds.enum';
const dispatchProps = { HideDialog };
const stateToProps = (state: State, ownProps: DialogProps): DialogProps => {
    const { dialog } = state;
    return {
        ...{ browser: state.browser },
        ...ownProps,
        ...dialog,
    };
};

const useStyles = makeStyles({
    paperWidthSm: {
        maxWidth: (props: { width?: string; height?: string }): string => `${props.width}`,
        maxHeight: (props: { width?: string; height?: string }): string => `${props.height}px`,
        '& .MuiDialogContent-root': {
            wordBreak: 'break-word',
        },
    },
});

interface DispatchProps {
    HideDialog(): void;
}

type Props = DialogProps & DispatchProps;

const Dialog: React.FC<DialogProps> = (props: Props): JSX.Element => {
    const dialogConfig = useAvailableDialogsConfig(props.id as DialogIds);
    const mergedProps = { ...props, ...dialogConfig };
    const {
        HideDialog,
        isOpened,
        body,
        title,
        width,
        height,
        isDisabled,
        isHiddenManually,
        confirmLabel = 'Ok',
        cancelLabel = 'Cancel',
        destructiveLabel = 'Delete',
        disableFooter,
        header,
        disabledContentPadding,
        disableHeader,
        customButtons,
        onClose,
    }: Props = mergedProps;

    const showCancelButton = cancelLabel || mergedProps.onClickCancel;
    const showAgreeButton = mergedProps.confirmLabel || mergedProps.onClickConfirm;
    const showDeleteButton = mergedProps.destructiveLabel || mergedProps.onClickDestructive;
    const showButtons = showAgreeButton || showDeleteButton || showCancelButton;

    const onConfirm = (): void => {
        if (mergedProps.onClickConfirm) {
            mergedProps.onClickConfirm();
        }
        if (!isHiddenManually) {
            HideDialog();
        }
    };

    const onCancel = (): void => {
        if (mergedProps.onClickCancel) {
            mergedProps.onClickCancel();
        }
        HideDialog();
    };

    const onDelete = (): void => {
        if (mergedProps.onClickDestructive) {
            mergedProps.onClickDestructive();
        }
        HideDialog();
    };

    const classes = useStyles({ width, height });

    return (
        <Portal>
            <ComponentDialog
                disableHeader={disableHeader}
                onCloseClick={() => {
                    onClose?.();
                    HideDialog?.();
                }}
                aria-labelledby="dialog-box"
                open={isOpened ? isOpened : false}
                classes={classes}
                body={<>{body}</>}
                title={title}
                disableFooter={disableFooter}
                header={header}
                disabledContentPadding={disabledContentPadding}
                customButtons={
                    customButtons ||
                    (showButtons && (
                        <Fragment>
                            {showAgreeButton && (
                                <PrimaryButton
                                    text={confirmLabel}
                                    onClick={onConfirm}
                                    disabled={isDisabled}
                                    isLoading={mergedProps.isLoading}
                                />
                            )}
                            {showDeleteButton && <DestructiveButton onClick={onDelete} text={destructiveLabel} />}
                            {showCancelButton && <SecondaryButton onClick={onCancel} text={cancelLabel} />}
                        </Fragment>
                    ))
                }
            />
        </Portal>
    );
};

export default connect<DialogProps, DispatchProps>(stateToProps, dispatchProps)(Dialog);
